import { getLogger } from '@signalwire/core';
import { getUserMedia, getMediaConstraints } from './utils/helpers';
import { sdpStereoHack, sdpBitrateHack, sdpMediaOrderHack, } from './utils/sdpHelpers';
import { sdpToJsonHack, RTCPeerConnection, streamIsValid, stopTrack, } from './utils/webrtcHelpers';
export default class RTCPeer {
    constructor(call, type) {
        this.call = call;
        this.type = type;
        this._negotiating = false;
        this.options = call.options;
        this.logger.debug('New Peer with type:', this.type, 'Options:', this.options);
        this._onIce = this._onIce.bind(this);
    }
    get logger() {
        return getLogger();
    }
    get isOffer() {
        return this.type === 'offer';
    }
    get isAnswer() {
        return this.type === 'answer';
    }
    get isSimulcast() {
        return this.options.simulcast === true;
    }
    get isSfu() {
        return this.options.sfu === true;
    }
    get localVideoTrack() {
        const videoSender = this._getSenderByKind('video');
        return (videoSender === null || videoSender === void 0 ? void 0 : videoSender.track) || null;
    }
    get localAudioTrack() {
        const audioSender = this._getSenderByKind('audio');
        return (audioSender === null || audioSender === void 0 ? void 0 : audioSender.track) || null;
    }
    get hasAudioSender() {
        return this._getSenderByKind('audio') ? true : false;
    }
    get hasVideoSender() {
        return this._getSenderByKind('video') ? true : false;
    }
    get hasAudioReceiver() {
        return this._getReceiverByKind('audio') ? true : false;
    }
    get hasVideoReceiver() {
        return this._getReceiverByKind('video') ? true : false;
    }
    get config() {
        const { rtcPeerConfig = {} } = this.options;
        const config = Object.assign({ bundlePolicy: 'max-compat', iceServers: this.call.iceServers, 
            // @ts-ignore
            sdpSemantics: 'unified-plan' }, rtcPeerConfig);
        this.logger.debug('RTC config', config);
        return config;
    }
    get localSdp() {
        var _a, _b;
        return (_b = (_a = this.instance) === null || _a === void 0 ? void 0 : _a.localDescription) === null || _b === void 0 ? void 0 : _b.sdp;
    }
    stopTrackSender(kind) {
        var _a, _b;
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender) {
                return this.logger.info(`There is not a '${kind}' sender to stop.`);
            }
            if (sender.track) {
                stopTrack(sender.track);
                (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.localStream) === null || _b === void 0 ? void 0 : _b.removeTrack(sender.track);
            }
        }
        catch (error) {
            this.logger.error('RTCPeer stopTrackSender error', kind, error);
        }
    }
    async restoreTrackSender(kind) {
        var _a, _b;
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender) {
                return this.logger.info(`There is not a '${kind}' sender to restore.`);
            }
            if (sender.track && sender.track.readyState !== 'ended') {
                return this.logger.info(`There is already an active ${kind} track.`);
            }
            const constraints = await getMediaConstraints(this.options);
            // @ts-ignore
            const stream = await getUserMedia({ [kind]: constraints[kind] });
            if (stream && streamIsValid(stream)) {
                const newTrack = stream.getTracks().find((t) => t.kind === kind);
                if (newTrack) {
                    await sender.replaceTrack(newTrack);
                    (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.localStream) === null || _b === void 0 ? void 0 : _b.addTrack(newTrack);
                }
            }
        }
        catch (error) {
            this.logger.error('RTCPeer restoreTrackSender error', kind, error);
        }
    }
    getDeviceId(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            const { deviceId = null } = sender.track.getSettings();
            return deviceId;
        }
        catch (error) {
            this.logger.error('RTCPeer getDeviceId error', kind, error);
            return null;
        }
    }
    getTrackSettings(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            return sender.track.getSettings();
        }
        catch (error) {
            this.logger.error('RTCPeer getTrackSettings error', kind, error);
            return null;
        }
    }
    getDeviceLabel(kind) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return null;
            }
            return sender.track.label;
        }
        catch (error) {
            this.logger.error('RTCPeer getDeviceLabel error', kind, error);
            return null;
        }
    }
    restartIceWithRelayOnly() {
        try {
            const config = this.instance.getConfiguration();
            if (config.iceTransportPolicy === 'relay') {
                return this.logger.warn('RTCPeer already with iceTransportPolicy relay only');
            }
            const newConfig = Object.assign(Object.assign({}, config), { iceTransportPolicy: 'relay' });
            this.instance.setConfiguration(newConfig);
            // @ts-ignore
            this.instance.restartIce();
        }
        catch (error) {
            this.logger.error('RTCPeer restartIce error', error);
        }
    }
    async applyMediaConstraints(kind, constraints) {
        try {
            const sender = this._getSenderByKind(kind);
            if (!sender || !sender.track) {
                return this.logger.info('No sender to apply constraints', kind, constraints);
            }
            if (sender.track.readyState === 'live') {
                const newConstraints = Object.assign(Object.assign({}, sender.track.getConstraints()), constraints);
                const deviceId = this.getDeviceId(kind);
                if (deviceId && !this.options.screenShare) {
                    newConstraints.deviceId = { exact: deviceId };
                }
                this.logger.info(`Apply ${kind} constraints`, this.call.id, newConstraints);
                await sender.track.applyConstraints(newConstraints);
            }
        }
        catch (error) {
            this.logger.error('Error applying constraints', kind, constraints);
        }
    }
    _getSenderByKind(kind) {
        if (!this.instance.getSenders) {
            this.logger.warn('RTCPeerConnection.getSenders() not available.');
            return null;
        }
        return this.instance
            .getSenders()
            .find(({ track }) => track && track.kind === kind);
    }
    _getReceiverByKind(kind) {
        if (!this.instance.getReceivers) {
            this.logger.warn('RTCPeerConnection.getReceivers() not available.');
            return null;
        }
        return this.instance
            .getReceivers()
            .find(({ track }) => track && track.kind === kind);
    }
    async startNegotiation(force = false) {
        var _a, _b;
        if (this._negotiating) {
            return this.logger.warn('Skip twice onnegotiationneeded!');
        }
        this._negotiating = true;
        try {
            /**
             * additionalDevice and screenShare are `sendonly`
             */
            if (this.options.additionalDevice || this.options.screenShare) {
                (_b = (_a = this.instance).getTransceivers) === null || _b === void 0 ? void 0 : _b.call(_a).forEach((tr) => {
                    tr.direction = 'sendonly';
                });
            }
            this.instance.removeEventListener('icecandidate', this._onIce);
            this.instance.addEventListener('icecandidate', this._onIce);
            if (this.isOffer) {
                this.logger.debug('Trying to generate offer');
                const offer = await this.instance.createOffer({
                    /**
                     * While this property is deprected, on Browsers where this
                     * is still supported this avoids conflicting with the VAD
                     * server-side
                     */
                    // @ts-ignore
                    voiceActivityDetection: false,
                });
                await this._setLocalDescription(offer);
            }
            if (this.isAnswer) {
                this.logger.debug('Trying to generate answer');
                await this._setRemoteDescription({
                    sdp: this.options.remoteSdp,
                    type: 'offer',
                });
                const answer = await this.instance.createAnswer({
                    // Same as above.
                    // @ts-ignore
                    voiceActivityDetection: false,
                });
                await this._setLocalDescription(answer);
            }
            /**
             * ReactNative Workaround
             */
            if (force) {
                this._sdpReady();
            }
        }
        catch (error) {
            this.logger.error(`Error creating ${this.type}:`, error);
        }
    }
    async onRemoteSdp(sdp) {
        try {
            const type = this.isOffer ? 'answer' : 'offer';
            await this._setRemoteDescription({ sdp, type });
            /**
             * Resolve the start() method only for Offer because for Answer
             * we need to reply to the server and wait for the signaling.
             */
            if (this.isOffer) {
                this._resolveStartMethod();
            }
        }
        catch (error) {
            this.logger.error(`Error handling remote SDP on call ${this.call.id}:`, error);
            this.call.hangup();
            this._rejectStartMethod(error);
        }
    }
    _setupRTCPeerConnection() {
        if (!this.instance) {
            this.instance = RTCPeerConnection(this.config);
            this._attachListeners();
        }
    }
    async start() {
        return new Promise(async (resolve, reject) => {
            this._resolveStartMethod = resolve;
            this._rejectStartMethod = reject;
            try {
                this.options.localStream = await this._retrieveLocalStream();
            }
            catch (error) {
                this._rejectStartMethod(error);
                return this.call.setState('hangup');
            }
            /**
             * We need to defer the creation of RTCPeerConnection
             * until we gain gUM access otherwise it will have
             * private IP addresses in ICE host candidates
             * replaced by an mDNS hostname
             * @see https://groups.google.com/g/discuss-webrtc/c/6stQXi72BEU?pli=1
             */
            this._setupRTCPeerConnection();
            const { localStream = null } = this.options;
            if (localStream && streamIsValid(localStream)) {
                const audioTracks = localStream.getAudioTracks();
                this.logger.debug('Local audio tracks: ', audioTracks);
                const videoTracks = localStream.getVideoTracks();
                this.logger.debug('Local video tracks: ', videoTracks);
                // FIXME: use transceivers way only for offer - when answer gotta match mid from the ones from SRD
                if (this.isOffer &&
                    typeof this.instance.addTransceiver === 'function') {
                    // Use addTransceiver
                    audioTracks.forEach((track) => {
                        this.instance.addTransceiver(track, {
                            direction: 'sendrecv',
                            streams: [localStream],
                        });
                    });
                    const transceiverParams = {
                        direction: 'sendrecv',
                        streams: [localStream],
                    };
                    if (this.isSimulcast) {
                        const rids = ['0', '1', '2'];
                        transceiverParams.sendEncodings = rids.map((rid) => ({
                            active: true,
                            rid: rid,
                            scaleResolutionDownBy: Number(rid) * 6 || 1.0,
                        }));
                    }
                    this.logger.debug('Applying video transceiverParams', transceiverParams);
                    videoTracks.forEach((track) => {
                        this.instance.addTransceiver(track, transceiverParams);
                    });
                    if (this.isSfu) {
                        const { msStreamsNumber = 5 } = this.options;
                        this.logger.debug('Add ', msStreamsNumber, 'recvonly MS Streams');
                        transceiverParams.direction = 'recvonly';
                        for (let i = 0; i < Number(msStreamsNumber); i++) {
                            this.instance.addTransceiver('video', transceiverParams);
                        }
                    }
                }
                else if (typeof this.instance.addTrack === 'function') {
                    // Use addTrack
                    audioTracks.forEach((track) => {
                        this.instance.addTrack(track, localStream);
                    });
                    videoTracks.forEach((track) => {
                        this.instance.addTrack(track, localStream);
                    });
                }
                else {
                    // Fallback to legacy addStream ..
                    // @ts-ignore
                    this.instance.addStream(localStream);
                }
            }
            if (this.isOffer) {
                if (this.options.negotiateAudio) {
                    this._checkMediaToNegotiate('audio');
                }
                if (this.options.negotiateVideo) {
                    this._checkMediaToNegotiate('video');
                }
            }
            else {
                this.startNegotiation();
            }
        });
    }
    _checkMediaToNegotiate(kind) {
        // addTransceiver of 'kind' if not present
        const sender = this._getSenderByKind(kind);
        if (!sender && this.instance.addTransceiver) {
            const transceiver = this.instance.addTransceiver(kind, {
                direction: 'recvonly',
            });
            this.logger.debug('Add transceiver', kind, transceiver);
        }
    }
    async _sdpReady() {
        clearTimeout(this._iceTimeout);
        this._iceTimeout = null;
        if (!this.instance.localDescription) {
            return;
        }
        const { sdp, type } = this.instance.localDescription;
        if (sdp.indexOf('candidate') === -1) {
            this.logger.debug('No candidate - retry \n');
            this.startNegotiation(true);
            return;
        }
        this.logger.debug('LOCAL SDP \n', `Type: ${type}`, '\n\n', sdp);
        this.instance.removeEventListener('icecandidate', this._onIce);
        try {
            await this.call.onLocalSDPReady(this.instance.localDescription);
        }
        catch (error) {
            this._rejectStartMethod(error);
        }
    }
    _onIce(event) {
        if (!this._iceTimeout) {
            this._iceTimeout = setTimeout(() => this._sdpReady(), this.options.iceGatheringTimeout);
        }
        if (event.candidate) {
            this.logger.debug('IceCandidate:', event.candidate);
            // @ts-expect-error
            this.call.emit('icecandidate', event);
        }
        else {
            this._sdpReady();
        }
    }
    _setLocalDescription(localDescription) {
        const { useStereo, googleMaxBitrate, googleMinBitrate, googleStartBitrate, } = this.options;
        if (localDescription.sdp && useStereo) {
            localDescription.sdp = sdpStereoHack(localDescription.sdp);
        }
        if (localDescription.sdp &&
            googleMaxBitrate &&
            googleMinBitrate &&
            googleStartBitrate) {
            localDescription.sdp = sdpBitrateHack(localDescription.sdp, googleMaxBitrate, googleMinBitrate, googleStartBitrate);
        }
        this.logger.debug('LOCAL SDP \n', `Type: ${localDescription.type}`, '\n\n', localDescription.sdp);
        return this.instance.setLocalDescription(localDescription);
    }
    _setRemoteDescription(remoteDescription) {
        if (remoteDescription.sdp && this.options.useStereo) {
            remoteDescription.sdp = sdpStereoHack(remoteDescription.sdp);
        }
        if (remoteDescription.sdp && this.instance.localDescription) {
            remoteDescription.sdp = sdpMediaOrderHack(remoteDescription.sdp, this.instance.localDescription.sdp);
        }
        const sessionDescr = sdpToJsonHack(remoteDescription);
        this.logger.debug('REMOTE SDP \n', `Type: ${remoteDescription.type}`, '\n\n', remoteDescription.sdp);
        return this.instance.setRemoteDescription(sessionDescr);
    }
    async _retrieveLocalStream() {
        if (streamIsValid(this.options.localStream)) {
            return this.options.localStream;
        }
        const constraints = await getMediaConstraints(this.options);
        return getUserMedia(constraints);
    }
    _attachListeners() {
        this.instance.addEventListener('signalingstatechange', () => {
            this.logger.debug('signalingState:', this.instance.signalingState);
            switch (this.instance.signalingState) {
                case 'stable':
                    // Workaround to skip nested negotiations
                    // Chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=740501
                    this._negotiating = false;
                    break;
                case 'have-local-offer': {
                    if (this.instance.iceGatheringState === 'complete') {
                        this._sdpReady();
                    }
                    break;
                }
                case 'closed':
                    // @ts-ignore
                    delete this.instance;
                    break;
                default:
                    this._negotiating = true;
            }
        });
        this.instance.addEventListener('negotiationneeded', () => {
            this.logger.debug('Negotiation needed event');
            this.startNegotiation();
        });
        this.instance.addEventListener('iceconnectionstatechange', () => {
            this.logger.debug('iceConnectionState:', this.instance.iceConnectionState);
        });
        this.instance.addEventListener('icegatheringstatechange', () => {
            this.logger.debug('iceGatheringState:', this.instance.iceGatheringState);
        });
        // this.instance.addEventListener('icecandidateerror', (event) => {
        //   this.logger.warn('IceCandidate Error:', event)
        // })
        this.instance.addEventListener('track', (event) => {
            // @ts-expect-error
            this.call.emit('track', event);
            if (this.isSfu) {
                // const notification = { type: 'trackAdd', event }
                // this.call._dispatchNotification(notification)
            }
            this.options.remoteStream = event.streams[0];
        });
        // @ts-ignore
        this.instance.addEventListener('addstream', (event) => {
            if (event.stream) {
                this.options.remoteStream = event.stream;
            }
        });
    }
}
//# sourceMappingURL=RTCPeer.js.map